import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Skeleton,
  Tooltip,
  IconButton,
  Chip,
  alpha,
  Divider,
  Alert
} from '@mui/material';
import {
  History as HistoryIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as EmptyIcon,
  Print as PrintIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  AutoFixHigh as AutoFillIcon
} from '@mui/icons-material';
import { doc, getDoc, runTransaction, Timestamp } from 'firebase/firestore';
import { useNotifications } from '@toolpad/core/useNotifications';
import { auth } from 'src/firebase/firebaseConfig';
import { db } from 'src/firebase/firebaseConfig';
import { ClientField, ClientFormInstance } from 'shared/types/appTypes';
import AppleMusicSongList from './AppleMusicSongList';
import { useAppleMusicSongs } from 'src/hooks/useAppleMusicSongs';
import { useReactToPrint } from 'react-to-print';
import { formatTimeTzNaive, formatDateValueTzNaive } from 'src/utils/formatDateTzNaive';
import { hasValue, isAnswerableField, getFormCompletionStatus } from 'src/utils/clientFormUtils';

interface ClientFormSummaryProps {
  eventId: string;
}

/** Format a Firestore Timestamp or Date for display (date + time) */
const formatTimestamp = (ts: any): string => {
  if (!ts) return '';
  const date = formatDateValueTzNaive(ts);
  const time = formatTimeTzNaive(ts);
  return time ? `${date}, ${time}` : date;
};

/** Format duration in ms to m:ss */
const formatDuration = (ms: number): string => {
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/** Render a human-readable value for a field */
const renderFieldValue = (field: ClientField): React.ReactNode => {
  if (!('value' in field) || !hasValue(field)) {
    return null;
  }

  const v = field.value;

  switch (field.type) {
    case 'shortText':
    case 'longText':
      return (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {String(v)}
        </Typography>
      );
    case 'number':
      return <Typography variant="body2">{v === '' ? '' : String(v)}</Typography>;
    case 'boolean':
      return (
        <Chip
          label={v === true ? 'Yes' : 'No'}
          size="small"
          color={v === true ? 'success' : 'default'}
          variant="outlined"
          sx={{ fontWeight: 500 }}
        />
      );
    case 'address': {
      const address = v as {
        addressLine1?: string;
        addressLine2?: string;
        city?: string;
        state?: string;
        postalCode?: string;
        country?: string;
      };
      const line1 = (address.addressLine1 || '').trim();
      const line2 = (address.addressLine2 || '').trim();
      const cityLine = [address.city, address.state, address.postalCode].filter(Boolean).join(', ');
      const country = (address.country || '').trim();

      return (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {[line1, line2, cityLine, country].filter(Boolean).join('\n')}
        </Typography>
      );
    }
    case 'date':
      return <Typography variant="body2">{formatDateValueTzNaive(v)}</Typography>;
    case 'time':
      return <Typography variant="body2">{formatTimeTzNaive(v)}</Typography>;
    case 'dateRange': {
      const range = v as { start: any; end: any };
      const start = range.start ? formatDateValueTzNaive(range.start) : '—';
      const end = range.end ? formatDateValueTzNaive(range.end) : '—';
      return (
        <Typography variant="body2">
          {start} &ndash; {end}
        </Typography>
      );
    }
    case 'timeRange': {
      const range = v as { start: any; end: any };
      const start = range.start ? formatTimeTzNaive(range.start) : '—';
      const end = range.end ? formatTimeTzNaive(range.end) : '—';
      return (
        <Typography variant="body2">
          {start} &ndash; {end}
        </Typography>
      );
    }
    case 'multipleChoice':
      return (
        <Typography variant="body2">
          {Array.isArray(v)
            ? v.filter((item) => String(item || '').trim().length > 0).join(', ')
            : String(v)}
        </Typography>
      );
    case 'imageChoice':
      return <Typography variant="body2">{String(v)}</Typography>;
    case 'appleMusicPlaylist':
      // Handled separately with rich track data
      return null;
    default:
      return <Typography variant="body2">{String(v)}</Typography>;
  }
};

/** Render the edit history tooltip content */
const EditHistoryTooltip: React.FC<{ field: ClientField; songMap: Map<string, string> }> = ({ field, songMap }) => {
  const history = ('editHistory' in field && field.editHistory) || [];

  if (history.length === 0) {
    return (
      <Box sx={{ p: 1, maxWidth: 320 }}>
        <Typography variant="caption" color="text.secondary">
          No edit history
        </Typography>
      </Box>
    );
  }

  // Format previous value display
  const formatPreviousValue = (value: any): string => {
    if (value == null) return '(empty)';
    if (field.type === 'appleMusicPlaylist' && Array.isArray(value)) {
      if (value.length === 0) return '(empty)';
      const songNames = value
        .map((id: string) => ({ id, name: songMap.get(id) || null }))
        .filter((item) => item.name !== null)
        .map((item) => item.name);
      return songNames.length > 0 ? songNames.join(', ') : `${value.length} songs`;
    }
    return JSON.stringify(value);
  };

  return (
    <Box sx={{ p: 1, maxWidth: 360 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
        Edit History
      </Typography>
      {[...history].reverse().map((entry, idx) => (
        <Box key={idx} sx={{ mb: idx < history.length - 1 ? 1 : 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {formatTimestamp(entry.modifiedAt)} &middot; {entry.modifiedBy}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic' }}>
            Previous: {formatPreviousValue(entry.previousValue)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const ClientFormSummary: React.FC<ClientFormSummaryProps> = ({ eventId }) => {
  const [formData, setFormData] = useState<ClientFormInstance | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const notifications = useNotifications();
  const [togglingIndex, setTogglingIndex] = useState<number | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Client Form - ${formData?.templateName || 'Responses'}`,
  });

  useEffect(() => {
    const loadForm = async () => {
      if (!eventId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const docRef = doc(db, 'clientForms', eventId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setFormData(docSnap.data() as ClientFormInstance);
        } else {
          setFormData(null);
        }
      } catch (err: any) {
        console.error('Error loading client form:', err);
        setError(err?.message || 'Failed to load form');
      } finally {
        setIsLoading(false);
      }
    };

    loadForm();
  }, [eventId]);

  // Stabilize fields array reference and filter to answerable fields only
  const fields = useMemo(() => {
    const allFields = formData?.fields || [];
    return allFields.filter(isAnswerableField);
  }, [formData]);
  const { filledCount, totalCount } = useMemo(() => getFormCompletionStatus(formData), [formData]);
  const finalizedCount = useMemo(() => fields.filter(f => 'finalized' in f && f.finalized).length, [fields]);

  /** Toggle the finalized flag on a field and persist to Firestore */
  const handleToggleFinalized = useCallback(async (displayIdx: number) => {
    if (!formData || !eventId) return;
    setTogglingIndex(displayIdx);
    try {
      const displayField = fields[displayIdx];
      // Find the real index in formData.fields (which includes video fields)
      const realIndex = formData.fields.findIndex(f => f === displayField);
      if (realIndex === -1) return;

      const localField = formData.fields[realIndex];
      if (localField.type === 'video') return;
      const nextFinalized = !localField.finalized;

      // Toggle only this field against the CURRENT server fields, so a stale local copy cannot undo
      // server-side changes (e.g. the event-date sync) or a client's answer saved since this loaded.
      const docRef = doc(db, 'clientForms', eventId);
      const serverFields = await runTransaction(db, async (tx) => {
        const snap = await tx.get(docRef);
        const current: ClientField[] = snap.exists() && Array.isArray(snap.data()?.fields)
          ? [...(snap.data() as ClientFormInstance).fields]
          : [];
        const serverField = current[realIndex];
        if (!serverField || serverField.type !== localField.type || serverField.label !== localField.label) {
          throw new Error('Client form changed since it was loaded');
        }
        current[realIndex] = { ...serverField, finalized: nextFinalized } as ClientField;
        tx.update(docRef, {
          fields: current,
          updatedAt: Timestamp.now(),
          updatedBy: auth.currentUser?.email || '',
        });
        return current;
      });
      setFormData({ ...formData, fields: serverFields });
    } catch (err) {
      console.error('Error toggling finalized:', err);
      notifications.show('Could not update this question. If the form changed since it loaded, reload and try again.', { severity: 'error', autoHideDuration: 6000 });
    } finally {
      setTogglingIndex(null);
    }
  }, [formData, eventId, fields, notifications]);

  // Collect all Apple Music song IDs across all playlist fields AND edit history
  // Use a serialized key to ensure we only recompute when IDs actually change
  const allSongIdsKey = useMemo(() => {
    const ids: string[] = [];
    fields.forEach((f) => {
      // Current values
      if (f.type === 'appleMusicPlaylist' && Array.isArray(f.value) && f.value.length > 0) {
        ids.push(...f.value);
      }
      // Edit history values
      if (f.type === 'appleMusicPlaylist' && 'editHistory' in f && f.editHistory) {
        f.editHistory.forEach((entry) => {
          if (Array.isArray(entry.previousValue)) {
            ids.push(...entry.previousValue);
          }
        });
      }
    });
    // Sort and dedupe, then create a stable string key
    const uniqueSorted = [...new Set(ids)].sort();
    return uniqueSorted.join(',');
  }, [fields]);

  // Convert back to array for the hook
  const allSongIds = useMemo(() => {
    return allSongIdsKey ? allSongIdsKey.split(',') : [];
  }, [allSongIdsKey]);

  // Fetch all songs and create a map
  const { songs } = useAppleMusicSongs(allSongIds);
  const songMap = useMemo(() => {
    const map = new Map<string, string>();
    songs.forEach((s) => map.set(s.id, s.name));
    return map;
  }, [songs]);

  if (isLoading) {
    return (
      <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
        <Skeleton variant="text" width={200} height={32} />
        <Skeleton variant="rectangular" height={120} sx={{ mt: 2, borderRadius: 1 }} />
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!formData || fields.length === 0) {
    return null; // No form assigned or no fields — don't render anything
  }

  return (
    <Paper
      ref={printRef}
      variant="outlined"
      sx={{
        mt: 3,
        overflow: 'hidden',
        '@media print': {
          breakInside: 'avoid',
          boxShadow: 'none',
          border: '1px solid #ccc',
          bgcolor: '#fff !important',
          '& .MuiBox-root, & .MuiStack-root, & .MuiPaper-root': {
            bgcolor: '#fff !important',
          },
          '& .MuiTypography-root': {
            color: '#000 !important',
          },
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Client Form Responses
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formData.templateName}
              {formData.updatedAt && <> &middot; Last updated {formatTimestamp(formData.updatedAt)}</>}
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Chip
              label={`${filledCount} / ${totalCount} answered`}
              size="small"
              color={filledCount === totalCount ? 'success' : 'default'}
              variant="outlined"
            />
            <Chip
              icon={<LockIcon sx={{ fontSize: 14 }} />}
              label={`${finalizedCount} / ${totalCount} finalized`}
              size="small"
              color={finalizedCount === totalCount ? 'info' : 'default'}
              variant="outlined"
            />
            <Tooltip title="Print or save as PDF">
              <IconButton
                onClick={handlePrint}
                size="small"
                sx={{
                  '@media print': { display: 'none' },
                }}
              >
                <PrintIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      {/* Fields list */}
      <Box sx={{ px: 3, py: 1 }}>
        {fields.map((field, idx) => {
          const filled = hasValue(field);
          const finalized = 'finalized' in field && !!field.finalized;
          const isAppleMusicPlaylist =
            field.type === 'appleMusicPlaylist' && Array.isArray(field.value) && field.value.length > 0;
          const hasAutoPopulate = 'autoPopulatedValue' in field && field.autoPopulatedValue !== undefined;
          const autoPopulateModified = hasAutoPopulate && JSON.stringify(('value' in field ? field.value : null)) !== JSON.stringify(field.autoPopulatedValue);
          const clientConfirmed = hasAutoPopulate && !!(field as any).clientConfirmed;

          return (
            <React.Fragment key={idx}>
              <Box
                sx={{
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  // Finalized fields get a cool blue tint
                  ...(finalized && {
                    bgcolor: (theme) => alpha(theme.palette.info.main, 0.04),
                    mx: -3,
                    px: 3
                  }),
                  // Unfilled (and not finalized) fields get a subtle warm tint
                  ...(!filled && !finalized && {
                    bgcolor: (theme) => alpha(theme.palette.warning.main, 0.04),
                    mx: -3,
                    px: 3
                  }),
                  '@media print': { 
                    pageBreakInside: 'avoid',
                    ...(finalized && {
                      bgcolor: '#eef4ff !important',
                    }),
                    ...(!filled && !finalized && {
                      bgcolor: '#f5f5f5 !important',
                    }),
                  }
                }}
              >
                {/* Status icon */}
                <Box sx={{ pt: 0.25, flexShrink: 0 }}>
                  {finalized ? (
                    <LockIcon sx={{ fontSize: 18, color: 'info.main' }} />
                  ) : filled ? (
                    <CheckIcon sx={{ fontSize: 18, color: 'success.main' }} />
                  ) : (
                    <EmptyIcon sx={{ fontSize: 18, color: 'warning.main', opacity: 0.6 }} />
                  )}
                </Box>

                {/* Label + value */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      color: finalized ? 'info.dark' : filled ? 'text.primary' : 'warning.dark'
                    }}
                  >
                    {field.label}
                    {finalized && (
                      <Chip
                        label="Finalized"
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ ml: 1, height: 18, fontSize: '0.65rem', fontWeight: 500 }}
                      />
                    )}
                    {hasAutoPopulate && !clientConfirmed && (
                      <Chip
                        icon={<AutoFillIcon sx={{ fontSize: 12 }} />}
                        label="Auto-filled"
                        size="small"
                        variant="outlined"
                        sx={{ ml: 1, height: 18, fontSize: '0.65rem', fontWeight: 500, color: 'text.secondary', borderColor: 'divider' }}
                      />
                    )}
                    {clientConfirmed && !autoPopulateModified && (
                      <Chip
                        icon={<CheckIcon sx={{ fontSize: 12 }} />}
                        label="Confirmed"
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ ml: 1, height: 18, fontSize: '0.65rem', fontWeight: 500 }}
                      />
                    )}
                    {clientConfirmed && autoPopulateModified && (
                      <Tooltip title={`Originally auto-filled: ${String(field.autoPopulatedValue ?? '')}`} arrow>
                        <Chip
                          icon={<AutoFillIcon sx={{ fontSize: 12 }} />}
                          label="Client modified"
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ ml: 1, height: 18, fontSize: '0.65rem', fontWeight: 500 }}
                        />
                      </Tooltip>
                    )}
                    {!filled && !finalized && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ ml: 1, color: 'warning.main', fontWeight: 400 }}
                      >
                        Not answered
                      </Typography>
                    )}
                  </Typography>

                  {field.type !== 'appleMusicPlaylist' && filled && (
                    <Box sx={{ mt: 0.25 }}>{renderFieldValue(field)}</Box>
                  )}

                  {/* Apple Music playlist — rendered as a rich track list */}
                  {isAppleMusicPlaylist && (
                    <Box sx={{ mt: 1 }}>
                      <AppleMusicSongList songIds={field.value} allSongIds={allSongIds} />
                    </Box>
                  )}
                </Box>

                {/* Finalize toggle + Last modified / edit history */}
                <Box
                  sx={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  {filled && (
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {formatTimestamp(field.modifiedAt)}
                    </Typography>
                  )}
                  <Tooltip
                    title={<EditHistoryTooltip field={field} songMap={songMap} />}
                    arrow
                    placement="left"
                    slotProps={{
                      tooltip: {
                        sx: {
                          maxWidth: 380,
                          bgcolor: 'background.paper',
                          color: 'text.primary',
                          boxShadow: 3,
                          border: '1px solid',
                          borderColor: 'divider'
                        }
                      },
                      arrow: { sx: { color: 'background.paper' } }
                    }}
                  >
                    <IconButton size="small" sx={{ '@media print': { display: 'none' } }}>
                      <HistoryIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={finalized ? 'Unlock for client editing' : 'Lock — finalize this field'} arrow>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleFinalized(idx)}
                      disabled={togglingIndex === idx}
                      sx={{
                        '@media print': { display: 'none' },
                        color: finalized ? 'info.main' : 'text.disabled',
                        '&:hover': {
                          color: finalized ? 'info.dark' : 'info.main',
                        },
                      }}
                    >
                      {finalized ? (
                        <LockIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <LockOpenIcon sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              {idx < fields.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </Box>
    </Paper>
  );
};

export default ClientFormSummary;
